import React, { useState, useEffect } from "react";
import API_URL from "../apiConfig";

export default function BatchManagement({ token, user }) {
  const [batches, setBatches] = useState([]);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", start_year: "", end_year: "", annual_fee: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const isAdmin = user?.role === "admin";

  // Helper to reload batches
  const loadBatches = () => {
    setLoading(true);
    setError("");
    fetch(`${API_URL}/api/batches`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.json())
      .then(setBatches)
      .catch(() => setError("Could not fetch batches"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadBatches();
    // eslint-disable-next-line
  }, [token]);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSave = async (id) => {
    if (!form.annual_fee || isNaN(Number(form.annual_fee)) || Number(form.annual_fee) < 0) {
      setError("Enter a valid non-negative annual fee.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/batches/${id}/annual-fee`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ annual_fee: Number(form.annual_fee) })
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.error || "Error updating annual fee");
      } else {
        setEditId(null);
        setForm({ name: "", start_year: "", end_year: "", annual_fee: "" });
        loadBatches();
      }
    } catch {
      setError("Server error");
    }
    setLoading(false);
  };

  const handleAdd = async () => {
    if (!form.name || !form.start_year || !form.end_year || !form.annual_fee ||
      isNaN(Number(form.start_year)) || isNaN(Number(form.end_year)) ||
      isNaN(Number(form.annual_fee)) || Number(form.annual_fee) < 0) {
      setError("Please fill all batch fields with valid values.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/batches`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          start_year: Number(form.start_year),
          end_year: Number(form.end_year),
          annual_fee: Number(form.annual_fee)
        })
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.error || "Error adding batch");
      } else {
        setForm({ name: "", start_year: "", end_year: "", annual_fee: "" });
        loadBatches();
      }
    } catch {
      setError("Server error");
    }
    setLoading(false);
  };

  return (
    <div className="batch-management-container">
      <h2>Batch/Annual Fee Management</h2>
      {isAdmin && (
        <div className="batch-form">
          <input name="name" value={form.name} onChange={handleChange} placeholder="Batch Name (e.g. 2025-26)" />
          <input name="start_year" type="number" value={form.start_year} onChange={handleChange} placeholder="Start Year" />
          <input name="end_year" type="number" value={form.end_year} onChange={handleChange} placeholder="End Year" />
          <input name="annual_fee" type="number" value={form.annual_fee} onChange={handleChange} placeholder="Annual Fee (₹)" min={0} />
          <button onClick={handleAdd} disabled={loading}>Add Batch</button>
        </div>
      )}
      {error && <div style={{ color: "red", margin: "8px 0" }}>{error}</div>}
      <table className="batch-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Start Year</th>
            <th>End Year</th>
            <th>Annual Fee (₹)</th>
            {isAdmin && <th>Action</th>}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={isAdmin ? 5 : 4}>Loading...</td>
            </tr>
          ) : batches.length === 0 ? (
            <tr>
              <td colSpan={isAdmin ? 5 : 4}>No batches found.</td>
            </tr>
          ) : (
            batches.map(b =>
              <tr key={b.id}>
                <td>{b.name}</td>
                <td>{b.start_year}</td>
                <td>{b.end_year}</td>
                <td>
                  {editId === b.id ? (
                    <input
                      type="number"
                      name="annual_fee"
                      value={form.annual_fee}
                      onChange={handleChange}
                      min={0}
                      disabled={loading}
                    />
                  ) : (
                    b.annual_fee
                  )}
                </td>
                {isAdmin && (
                  <td>
                    {editId === b.id
                      ? <button onClick={() => handleSave(b.id)} disabled={loading}>Save</button>
                      : <button onClick={() => { setEditId(b.id); setForm(f => ({ ...f, annual_fee: b.annual_fee })); }}>Edit</button>
                    }
                  </td>
                )}
              </tr>
            )
          )}
        </tbody>
      </table>
    </div>
  );
}